import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Link, NavLink } from "@/components/LLink";
import {
  ArrowRight, Building2, ChevronDown, Columns3, DraftingCompass, Flame, Globe, Grid2x2,
  Layers, Menu, Phone, Scissors, Shield, Sofa, Sparkles, Sun, TreePine, Truck, Warehouse, X, Zap, Car,
} from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import { CONTACT } from "@/data/site";
import { LANGUAGES, useLang, useSite } from "@/i18n";

const ICONS = {
  "debitare-laser": Zap, "indoire-abkant": Layers, "vopsitorie-electrostatica": Sparkles, "sudura": Flame,
  "mobilier-metalic": Sofa, "porti-garduri": Shield, "carport": Car, "balustrade": Columns3,
  "panouri-decorative": Grid2x2, "terase": Sun, "structuri-metalice": Warehouse, "mobilier-urban": TreePine,
  "b2b": Building2, "arhitecti": DraftingCompass, "export": Truck,
};

const buildMenus = (t, SERVICES, PRODUCTS) => ({
  servicii: {
    kicker: t("hdr_services_k"),
    items: SERVICES.map((s) => ({ slug: s.slug, name: s.name, short: s.short, image: s.image, href: `/servicii/${s.slug}` })),
    allHref: "/servicii", allLabel: t("hdr_all_services"),
  },
  produse: {
    kicker: t("hdr_products_k"),
    items: PRODUCTS.map((p) => ({ slug: p.slug, name: p.name, short: p.short, image: p.image, href: `/produse/${p.slug}` })),
    allHref: "/productie", allLabel: t("hdr_all_production"),
  },
  parteneri: {
    kicker: t("hdr_partners_k"),
    items: [
      { slug: "b2b", name: t("hdr_b2b_t"), short: t("hdr_b2b_x"), image: "/images/b2b.jpg", href: "/b2b" },
      { slug: "arhitecti", name: t("hdr_arch_t"), short: t("hdr_arch_x"), image: "/images/arhitecti.jpg", href: "/arhitecti" },
      { slug: "export", name: t("nav_export"), short: t("hdr_export_x"), image: "/images/structuri.jpg", href: "/export" },
    ],
    allHref: "/portofoliu", allLabel: t("hdr_all_portfolio"),
  },
});

function MegaPanel({ menu, onClose }) {
  const [active, setActive] = useState(0);
  const item = menu.items[active] || menu.items[0];
  const cols = menu.items.length > 4 ? "sm:grid-cols-2" : "sm:grid-cols-1";
  return (
    <div
      className="absolute inset-x-0 top-full z-40 hidden justify-center px-6 pt-2 lg:flex"
      data-testid="mega-menu"
    >
      <div className="grid w-full max-w-6xl grid-cols-[1fr_320px] overflow-hidden rounded-xl border border-ink/10 bg-base shadow-2xl shadow-black/15">
        <div className="p-8">
          <div className={`grid gap-x-10 ${cols}`}>
            {menu.items.map((it, i) => {
              const Icon = ICONS[it.slug] || Grid2x2;
              return (
                <Link
                  key={it.href}
                  to={it.href}
                  onClick={onClose}
                  onMouseEnter={() => setActive(i)}
                  data-testid={`nav-link-${it.href.replaceAll("/", "-")}`}
                  className={`group flex items-start gap-4 border-b border-ink/[0.07] py-3.5 transition-colors ${active === i ? "text-ink" : "text-ink/85"}`}
                >
                  <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border transition-colors duration-300 ${active === i ? "border-gold/40 bg-gold/10 text-gold" : "border-ink/10 text-ink/50"}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[15px] font-semibold leading-tight">{it.name}</span>
                    <span className="mt-1 block truncate text-[13px] text-ink/50">{it.short}</span>
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="mt-6 flex items-center justify-end">
            <Link to={menu.allHref} onClick={onClose} data-testid="mega-menu-all" className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:text-gold">
              {menu.allLabel} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
        <Link to={item.href} onClick={onClose} className="relative hidden overflow-hidden border-l border-ink/10 bg-surface lg:block" data-testid="mega-menu-preview">
          <img key={item.image} src={item.image} alt={item.name} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-6 pt-16">
            <p className="text-lg font-semibold text-white">{item.name}</p>
          </div>
        </Link>
      </div>
    </div>
  );
}

const LangMenu = () => {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const current = LANGUAGES.find((l) => l.code === lang);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="lang-menu-button"
        className="flex items-center gap-1.5 rounded-md px-2 py-2 text-[13px] font-medium text-ink/70 transition-colors hover:text-ink"
      >
        <Globe className="h-4 w-4" />
        {current.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-2 w-40 overflow-hidden rounded-lg border border-ink/10 bg-base shadow-xl shadow-black/10" data-testid="lang-menu">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => { setLang(l.code); setOpen(false); }}
                data-testid={`lang-option-${l.code}`}
                className={`flex w-full items-center justify-between px-4 py-2.5 text-[13px] transition-colors ${
                  lang === l.code ? "bg-ink/5 font-semibold text-ink" : "text-ink/65 hover:bg-ink/5 hover:text-ink"
                }`}
              >
                <span>{l.name}</span>
                <span className="text-[11px] font-semibold uppercase opacity-50">{l.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const navCls = ({ isActive }) =>
  `relative flex items-center py-2 text-[15px] font-medium transition-colors hover:text-ink after:absolute after:inset-x-0 after:-bottom-[28px] after:h-[2px] after:bg-gold after:transition-transform after:duration-300 ${
    isActive ? "text-ink after:scale-x-100" : "text-ink/65 after:scale-x-0"
  }`;

export default function Header() {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t, lp, path } = useLang();
  const { services: SERVICES, products: PRODUCTS } = useSite();
  const MENUS = buildMenus(t, SERVICES, PRODUCTS);
  const NAV_SERVICES = SERVICES.map((s) => ({ name: s.name, href: `/servicii/${s.slug}` }));
  const NAV_PRODUCTS = PRODUCTS.map((p) => ({ name: p.name, href: `/produse/${p.slug}` }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMenu(null); setOpen(false); }, [pathname]);

  const Trigger = ({ id, label }) => {
    const isOpen = menu === id;
    const isActive = path.startsWith(`/${id}`) || (id === "parteneri" && ["/b2b", "/arhitecti", "/export"].includes(path));
    return (
      <button
        type="button"
        onMouseEnter={() => setMenu(id)}
        onClick={() => (id === "servicii" ? navigate(lp("/servicii")) : setMenu(isOpen ? null : id))}
        data-testid={`nav-dropdown-${id}`}
        aria-expanded={isOpen}
        className={`relative flex items-center gap-1 py-2 text-[15px] font-medium transition-colors hover:text-ink after:absolute after:inset-x-0 after:-bottom-[28px] after:h-[2px] after:bg-gold after:transition-transform after:duration-300 ${
          isOpen || isActive ? "text-ink after:scale-x-100" : "text-ink/65 after:scale-x-0"
        }`}
      >
        {label}
        <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>
    );
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b bg-base transition-[box-shadow,border-color] duration-300 ${
        scrolled || menu ? "border-ink/10 shadow-lg shadow-black/5" : "border-transparent"
      }`}
      onMouseLeave={() => setMenu(null)}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-[88px] max-w-[1400px] items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo" className="flex shrink-0 items-center gap-3">
          <img src="/logo.png" alt="HEAVY METAL" className="h-[74px] w-auto" />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex" data-testid="nav-desktop">
          <Trigger id="servicii" label={t("nav_services")} />
          <Trigger id="produse" label={t("nav_production")} />
          <Trigger id="parteneri" label={t("nav_partners")} />
          <span onMouseEnter={() => setMenu(null)}>
            <NavLink to="/portofoliu" data-testid="nav-link-portofoliu" className={navCls}>{t("nav_portfolio")}</NavLink>
          </span>
          <span onMouseEnter={() => setMenu(null)}>
            <NavLink to="/contact" data-testid="nav-link-contact" className={navCls}>{t("nav_contact")}</NavLink>
          </span>
        </nav>

        <div className="hidden items-center gap-3 lg:flex" onMouseEnter={() => setMenu(null)}>
          <LangMenu />
          <ThemeToggle />
          <button
            onClick={() => navigate(lp("/contact"))}
            data-testid="nav-cta-oferta"
            className="group ml-2 flex items-center gap-2 bg-ink px-6 py-3 text-[14px] font-semibold text-base transition-colors duration-300 hover:bg-gold"
          >
            {t("nav_cta")}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>

        <button className="text-ink lg:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Meniu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {menu && <MegaPanel key={menu} menu={MENUS[menu]} onClose={() => setMenu(null)} />}

      {open && (
        <div className="max-h-[calc(100vh-88px)] overflow-y-auto border-t border-ink/10 bg-base px-5 py-6 lg:hidden" data-testid="nav-mobile">
          <div className="flex flex-col gap-3 text-[15px] font-medium">
            <Link to="/servicii" onClick={() => setOpen(false)} data-testid="nav-mobile-servicii" className="text-ink">{t("nav_services")}</Link>
            {NAV_SERVICES.map((s) => (
              <Link key={s.href} to={s.href} onClick={() => setOpen(false)} data-testid={`nav-mobile${s.href.replaceAll("/", "-")}`} className="pl-3 text-ink/70">{s.name}</Link>
            ))}
            <Link to="/productie" onClick={() => setOpen(false)} data-testid="nav-mobile-productie" className="pt-3 text-ink">{t("nav_production")}</Link>
            {NAV_PRODUCTS.map((p) => (
              <Link key={p.href} to={p.href} onClick={() => setOpen(false)} data-testid={`nav-mobile${p.href.replaceAll("/", "-")}`} className="pl-3 text-ink/70">{p.name}</Link>
            ))}
            <Link to="/portofoliu" onClick={() => setOpen(false)} data-testid="nav-mobile-portofoliu" className="text-ink">{t("nav_portfolio")}</Link>
            <Link to="/b2b" onClick={() => setOpen(false)} data-testid="nav-mobile-b2b" className="text-ink">B2B</Link>
            <Link to="/arhitecti" onClick={() => setOpen(false)} data-testid="nav-mobile-arhitecti" className="text-ink">{t("b2b_link2").replace(" ↗", "")}</Link>
            <Link to="/export" onClick={() => setOpen(false)} data-testid="nav-mobile-export" className="text-ink">{t("nav_export")}</Link>
            <Link to="/contact" onClick={() => setOpen(false)} data-testid="nav-mobile-contact" className="text-ink">{t("nav_contact")}</Link>
            <a href={CONTACT.phoneHref} data-testid="nav-mobile-phone" className="mt-2 flex items-center gap-2 text-gold">
              <Phone className="h-4 w-4" /> {CONTACT.phone}
            </a>
            <button onClick={() => { setOpen(false); navigate(lp("/contact")); }} data-testid="nav-mobile-cta" className="mt-2 flex items-center justify-center gap-2 bg-ink py-3 text-[14px] font-semibold text-base">
              {t("nav_cta")} <ArrowRight className="h-4 w-4" />
            </button>
            <div className="flex flex-wrap items-center gap-2 border-t border-ink/10 pt-4">
              {LANGUAGES.map((l) => <LangChip key={l.code} lang={l} />)}
              <ThemeToggle />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

const LangChip = ({ lang }) => {
  const { lang: active, setLang } = useLang();
  return (
    <button
      onClick={() => setLang(lang.code)}
      data-testid={`lang-chip-${lang.code}`}
      className={`rounded-full border px-4 py-1.5 text-[12px] font-semibold uppercase transition-colors ${
        active === lang.code ? "border-ink bg-ink text-base" : "border-ink/15 text-ink/60"
      }`}
    >
      {lang.label}
    </button>
  );
};
